import Facade from '@/Facade'
import { IInitResponse, Orientation, ShowRegularWinningsData } from '@/modules'
import GameSignals from '@/signals/GameSignals'
import gsap from 'gsap'
import { UIAPI } from '@modules/src/managers/UIAPI'
import { UIEvent, UIGameEvent } from '@modules/src/enums/UIEvents'
import { TranslationUtils } from '@modules/src/utils/TranslationUtils'
import { toMajorUnits } from '@/utils/CurrencyUnits'
import { Utils } from '@modules/src/utils/Utils'
import { RestoreUtil } from '@/utils/RestoreUtil'
import { Model } from '@/model/Model'
import { Utilities } from '@base/utils/Utilities'
import { Platform } from '@base/utils/Platform'
import { ModuleSignals } from '@modules/src/signals/ModuleSignals'
import { toMoney } from '@base/utils/Formatter'
import { getNetworkCodeFromSite, resolveOperatorConfig } from '@modules/src/operatorConfig'
import { OperatorFeatureConfig } from '@modules/src/operatorConfig/types'

export class UIManager {
    private static readonly LEGACY_IDLE_TEXT = 'Click SPIN to play'
    private static readonly LEGACY_SPIN_START_TEXT = 'Good Luck!'
    private static readonly LEGACY_WINNING_COMBO_PREFIX = 'Winning Combo'
    private _model: Model
    private _currentUIState: string
    private _isSpacebarActive: boolean
    private _isQuickSpinActive: boolean
    private _isGameLocked: boolean
    private _isMenuShown: boolean
    private _isDialogShown: boolean
    private _isSkipKeyUp: boolean
    private _infoTextObject: { textCounter: number; spinCounter: number; text: string; shownIndexes: number[] }
    private _uiPanelRoundWinTotal: number
    private _winningComboTickerDelay: gsap.core.Tween | null = null
    private _operatorConfig: OperatorFeatureConfig = {}
    private static UI_INFO_TEXT_SIZE: number = 9.7
    private static readonly DEFAULT_VOLUME: number = 0.5
    private static readonly UNMUTE_VOLUME: number = 0.5

    public constructor(model: Model) {
        this.setGameState('not_ready')

        this._isGameLocked = false
        this._isSpacebarActive = true
        this._model = model
        this._isSkipKeyUp = false
        this._uiPanelRoundWinTotal = 0
        this._infoTextObject = {
            textCounter: 0,
            spinCounter: 0,
            text: '',
            shownIndexes: [],
        }

        GameSignals.init.connect(this.onInit)
        GameSignals.displaySplashScreenState.connect(this.onGameIsReady)
        GameSignals.completeSplashScreenState.connect(this.onCompleteSplashScreenState)
        GameSignals.gamePause.connect(this.stopAutoPlay)
        GameSignals.gamePause.connect(this.onGamePause)
        GameSignals.gameResume.connect(this.onGameResume)
        GameSignals.stopAutoplay.connect(this.stopAutoPlay)
        GameSignals.updateAutoplayValue.connect(this.updateAutoplayValue)
        GameSignals.loadingProgress.connect(this.onLoadingProgress)
        GameSignals.betHasChanged.connect(this.onBetHasChanged)
        GameSignals.startSpin.connect(this.onStartSpin)
        GameSignals.updateWinPanelValue.connect(this.onUpdateWinPanelValueForUi)
        GameSignals.betCooldownEnded.connect(this.onStopSpin)
        GameSignals.balanceHasChanged.connect(this.onBalanceUpdate)
        GameSignals.updateUIWinField.connect(this.onUpdateUIWinField)
        GameSignals.lockGamePanel.connect(this.onLockGamePanel)
        GameSignals.unLockGamePanel.connect(this.onUnlockGamePanel)
        GameSignals.bonusStartComplete.connect(this.onBonusStartComplete)
        GameSignals.restoreFreeSpins.connect(this.updateFreeSpinsInfo.bind(this, true))
        GameSignals.showFreeSpinsSummary.connect(this.onFreeSpinsSummary)
        GameSignals.showFreeSpinsSummaryComplete.connect(this.onFreeRoundSpin.bind(this, true))
        GameSignals.bonusStart.connect(this.onBonusStart)
        GameSignals.bonusStopComplete.connect(this.onBonusStopComplete)
        ;(GameSignals as any).promoFreeSpinsAccepted?.connect(this.onPromoFreeSpinsAccepted)
        GameSignals.serverError.connect(this.onServerError)
        GameSignals.realityCheck.connect(this.onServerError)
        GameSignals.sessionTimeout.connect(this.onServerError)
        GameSignals.errorConnection.connect(this.onServerError)
        GameSignals.hardwareAcceleration.connect(() => {
            this.displayUI(true)
        })
        GameSignals.freeRoundsActive.connect(this.onFreeRoundSpin.bind(this, true))
        GameSignals.roundStart.connect(this.onFreeRoundSpin)
        GameSignals.mute.connect(this.mute)
        GameSignals.unMute.connect(this.unMute)
        GameSignals.toggleTurbo.connect(this.toggleTurboSpin)
        GameSignals.togglePaytayble.connect(this.onTogglePaytable)
        GameSignals.toggleGameHelp.connect(this.onToggleGameHelp)
        GameSignals.betPlus.connect(this.onBetPlusClicked)
        GameSignals.betMinus.connect(this.onBetMinusClicked)
        GameSignals.skipIntroToggled.connect(this.toggleSkipIntro)
        GameSignals.premiumSpinsToggled.connect(this.togglePremiumSpinsMode)
        GameSignals.roundStop.connect(this.onRoundStop)
        GameSignals.idle.connect(this.onIdleState)
        GameSignals.showUI.connect(this.displayUI)
        GameSignals.fullScreen.connect(this.fullScreen)
        GameSignals.showRegularWin.connect(this.onShowRegularWin)

        UIAPI.emit(UIGameEvent.balance, { value: this._model.balance })
        GameSignals.balanceUpdate.emit(this._model.balance)
    }

    private onGamePause = () => {
        this._isGameLocked = true
    }

    private onGameResume = () => {
        this._isGameLocked = false
    }

    private toggleSkipIntro = (state: boolean) => {
        RestoreUtil.setKeyData(RestoreUtil.SKIP_INTRO_KEY, `${state}`)
        UIAPI.emit(UIGameEvent.show_splash, { value: !state })
    }
    private fullScreen = () => {
        UIAPI.emit(UIEvent.set_fullscreen, { value: true })
    }
    private addKeyboardListener = () => {
        if (this._isGameLocked) return

        // document.addEventListener('keydown', (event) => {
        //     //SETS PLAY BUTTON AS PRESSED
        //     if (event.code == 'Space') {
        //         UIAPI.emit(UIGameEvent.game_state, {
        //             state: this._currentUIState,
        //             mods: {
        //                 play: 'pressed',
        //             },
        //         })
        //     } else if (event.code == 'KeyF') {
        //         UIAPI.emit(UIEvent.set_fullscreen, false)
        //     }
        // })

        document.addEventListener('keyup', (event) => {
            if (event.code == 'Space') {
                if (!this._isSpacebarActive || this._isMenuShown || this._isDialogShown) {
                    event.preventDefault()
                    return
                }

                if (this._isSkipKeyUp) {
                    this._isSkipKeyUp = false
                    return
                }

                switch (this._currentUIState) {
                    case 'ready':
                        UIAPI.emit(UIEvent.clicked_play)
                        UIAPI.emit(UIEvent.set_fullscreen)
                        break
                    case 'playing':
                        if (this._isQuickSpinActive) {
                            GameSignals.stopButtonClicked.emit()
                        }
                        break
                }
            }
            // else {
            //     if (event.code == 'KeyF') {
            //         console.log('KEY F')
            //         UIAPI.emit(UIEvent.set_fullscreen, true)
            //     }
            // }
        })
    }

    private onInit = (initData: IInitResponse, reInit: boolean = false) => {
        this._operatorConfig = this.resolveOperatorRuntimeConfig(initData)
        this.applyOperatorConfigToDefaults(this._operatorConfig)

        const quickSpinAllowedByOperator = this._operatorConfig.isQuickSpin !== false && this._operatorConfig.noTurboSpin !== true
        const autoplayAllowedByOperator = this._operatorConfig.noAutoPlay !== true

        this._isQuickSpinActive = quickSpinAllowedByOperator && Facade.urlParams.quickSpin !== 'false'

        if (!reInit) {
            UIAPI.connectAll(this.onUIConnections)
        }

        const isAutoPlayEnabled = autoplayAllowedByOperator && Facade.urlParams.autoSpin !== 'false'

        const localStorageData = {
            music: RestoreUtil.getKeyData(RestoreUtil.MUSIC_KEY),
            sound: RestoreUtil.getKeyData(RestoreUtil.SOUND_KEY),
            volume: RestoreUtil.getKeyData(RestoreUtil.VOLUME_KEY),
            skipIntro: RestoreUtil.getKeyData(RestoreUtil.SKIP_INTRO_KEY),
            spacebarSpin: RestoreUtil.getKeyData(RestoreUtil.SPACEBAR_SPIN_KEY),
            turboSpin: RestoreUtil.getKeyData(RestoreUtil.TURBO_SPIN_KEY),
        }

        const fallbackVolume =
            typeof Facade.defaultConfig?.volume === 'number' ? Facade.defaultConfig.volume : UIManager.DEFAULT_VOLUME
        const persistedVolume = Number(localStorageData.volume)
        const fallbackMuted = localStorageData.sound === 0 && localStorageData.music === 0
        const volume = this.clampVolume(
            Number.isFinite(persistedVolume) ? persistedVolume : fallbackMuted ? 0 : fallbackVolume
        )
        const hasVolume = volume > 0

        this._isSpacebarActive = Number.isInteger(localStorageData.spacebarSpin)
            ? !!localStorageData.spacebarSpin
            : Facade.defaultConfig.spacebarSpin
        const storedTurboValue = Number.isInteger(localStorageData.turboSpin)
            ? !!localStorageData.turboSpin
            : Facade.defaultConfig.turboSpin
        Facade.isTurboSpin = quickSpinAllowedByOperator ? !!storedTurboValue : false
        Facade.isQuickSpin = Facade.isTurboSpin
        this._isQuickSpinActive = Facade.isTurboSpin

        const balance = this._model.balance
        const isBuyBonusEnabledForRuntime = this.isBuyBonusEnabledForRuntime(this._operatorConfig)
        const hideHomeForCurrentDevice = Platform.isMobile()
            ? this._operatorConfig.hideHomeButtonMobile === true
            : this._operatorConfig.hideHomeButtonInDesktop === true
        const shouldShowHistoryButton = this._operatorConfig.hideHistoryButton !== true
        const shouldShowSpacebarOption =
            !(this._operatorConfig.customBehaviorKeys || []).includes('HIDE_SPACEBAR_SETTINGS_HINT')
        const runtimeLobbyUrl = this.resolveLobbyUrl()
        const runtimeDepositUrl = this.resolveDepositUrl()

        const data: any = {
            game_version: Facade.gameVersion,
            casino_token: '',
            bonus_buy_enabled: isBuyBonusEnabledForRuntime,
            premium_spin_text_visible: false,
            bonus_buy_text_visible: isBuyBonusEnabledForRuntime,
            bonus_buy_items: this.getBonusBuyItems(),
            balance: balance,
            currency_symbol: this._model.initData.player.currency,
            home_url: hideHomeForCurrentDevice ? '' : runtimeLobbyUrl || '#',
            show_win_history: shouldShowHistoryButton,
            lobby_url: hideHomeForCurrentDevice ? '' : runtimeLobbyUrl,
            show_lobby_button: !hideHomeForCurrentDevice,
            show_home_button: !hideHomeForCurrentDevice,
            deposit_url: this._operatorConfig.hideDeposit === true ? '' : runtimeDepositUrl,
            auto_play_arr: [],
            stop_min_single_win: 0,
            stop_max_single_win: 100,
            max_bet: this._model.maxBet,
            stop_balance_low_arr: [10, 20, 30, 40],
            stop_balance_high_arr: [10, 20, 30, 40],
            bets_arr: this._model.bets,
            current_bet: +this._model.currentBet,
            current_base_bet: +this._model.currentBaseBet,
            current_auto_play: 0,
            current_stop_single_win: 0,
            current_stop_balance_low: 0,
            current_stop_balance_high: 0,
            sound: hasVolume,
            music: hasVolume,
            volume,
            spacebar_spin: this._isSpacebarActive,
            payout: Facade.payouts,
            rtp: +this._model.initData.game.rtp,
            bonus_rtp: 0,
            premium_rtp: 0,
            autoplay_enabled: isAutoPlayEnabled,
            quickspin_enabled: this._isQuickSpinActive,
            turbo_spin_enabled: quickSpinAllowedByOperator,
            display_max_win: true,
            country: '',
            max_win_text: '1x10000',
            notification_server_url: '',
            notification_server_token: '',
            show_splash_toggle_enabled: true,
            show_spacebar: shouldShowSpacebarOption,
        }
        GameSignals.balanceUpdate.emit(this._model.balance)
        UIAPI.emit(UIGameEvent.init, data)

        if (!reInit) {
            this.addKeyboardListener()
        }

        const showSplash: boolean =
            localStorageData.skipIntro !== null ? !localStorageData.skipIntro : !Facade.defaultConfig.skipIntro

        UIAPI.emit(UIGameEvent.show_splash, { value: showSplash })
        UIAPI.emit(UIGameEvent.turbo_spin, { value: Facade.isTurboSpin })
        UIAPI.emit(UIGameEvent.volume, { value: volume })
        GameSignals.setMasterVolume.emit(volume)

        GameSignals.betHasChanged.emit(this._model.currentBet, this._model.currentBaseBet)

        this.setGameState('ready')
    }

    private isLocalOrStagingRuntime = (): boolean => {
        if (typeof window === 'undefined') return false
        const host = (window.location.hostname || '').toLowerCase()
        return host === 'localhost' || host === '127.0.0.1' || host.includes('staging')
    }

    private parseBoolean(value: unknown): boolean {
        if (typeof value === 'boolean') return value
        if (typeof value === 'number') return value === 1
        const normalized = String(value ?? '')
            .trim()
            .toLowerCase()
        return normalized === '1' || normalized === 'true' || normalized === 'yes' || normalized === 'on'
    }

    private resolveOperatorRuntimeConfig(initData: IInitResponse): OperatorFeatureConfig {
        const params: any = Facade.urlParams || {}
        const siteCode = String(params.siteCode || '').trim()
        const networkCode = String(params.networkCode || '').trim() || getNetworkCodeFromSite(siteCode)

        const resolved = resolveOperatorConfig({
            siteCode,
            networkCode,
            runtime: {
                amountType: initData?.previous_round?.amount_type,
                lobbyUrl: this.resolveLobbyUrl(),
                depositUrl: this.resolveDepositUrl(),
                gameName: params.gameCode || params.game_code,
                gameOnlyPortraitMode: Facade.orientation === Orientation.PORTRAIT,
                dir: params.dir,
                hidePromoBtn: this.parseBoolean(params.hide_promo_btn || params.hidePromoBtn),
                isPmgGames: String(params.platform || '').toLowerCase().includes('pmg'),
            },
        })

        ;(Facade as any).operatorFeatureConfig = resolved
        return resolved
    }

    private applyOperatorConfigToDefaults(config: OperatorFeatureConfig): void {
        if (!Facade.defaultConfig) {
            return
        }

        if (config.isQuickSpin === false || config.noTurboSpin === true) {
            Facade.defaultConfig.turboSpin = false
        }

        if (config.noSwipeUp === true) {
            Facade.defaultConfig.mobileSwipeSpin = { enabled: false }
        }

        if (config.hideBuyBonus === true) {
            ;(Facade.defaultConfig as any).hideBuyBonus = true
        }
    }

    private resolveLobbyUrl(): string {
        const params: any = Facade.urlParams || {}
        return params.lobby_url || params.home_url || params.lobbyUrl || params.homeUrl || ''
    }

    private resolveDepositUrl(): string {
        const params: any = Facade.urlParams || {}
        return params.deposit_url || params.depositUrl || ''
    }

    private isBuyBonusEnabledForRuntime = (operatorConfig?: OperatorFeatureConfig): boolean => {
        if (operatorConfig?.hideBuyBonus === true) {
            return false
        }

        return this.isLocalOrStagingRuntime()
    }

    private getBonusBuyItems = (): any[] => {
        return []
    }

    private togglePremiumSpinsMode = (state: boolean) => {
        this._model.isPremiumSpinsActive = state
        this.onInit(null, true)
        UIAPI.emit(UIGameEvent.bonus_mode, { value: state })
    }

    private onLoadingProgress = (progress: number) => {
        UIAPI.emit('loading', { value: progress })
    }

    private onGameIsReady = () => {
        this._currentUIState = 'ready'
        UIAPI.emit(UIGameEvent.game_ready, {})
    }

    private stopAutoPlay = () => {
        UIAPI.emit(UIGameEvent.autoplay, { value: 0 })
    }

    private updateAutoplayValue = (value: number) => {
        UIAPI.emit(UIGameEvent.autoplay, { value })
    }

    private onCompleteSplashScreenState = () => {
        gsap.to(this._infoTextObject, {
            duration: 1,
            onComplete: () => {
                const text = this.getLegacyLikeIdlePrompt()
                this.updateInfoText(text)
            },
        })
    }

    private onIdleState = () => {
        if (this._model.freeRoundsActive()) {
            return
        }

        gsap.to(this._infoTextObject, {
            duration: 30,
            onComplete: () => {
                const text = this.getLegacyLikeIdlePrompt()
                this.updateInfoText(text)
                this._infoTextObject.textCounter = 0
                this._infoTextObject.spinCounter = 0
                this._infoTextObject.shownIndexes = []
            },
        })
    }

    private updateInfoTextRandomly = () => {
        gsap.killTweensOf(this._infoTextObject)
        let texts: string[] = []
        ;['uiText2', 'uiText3', 'uiText4', 'uiText5'].forEach((key: string) => {
            texts.push(TranslationUtils.translate(key))
        })

        if (!this._infoTextObject.shownIndexes.length && !this._infoTextObject.spinCounter) {
            this._infoTextObject.spinCounter++
            const shownIndex: number = 0
            this._infoTextObject.shownIndexes.push(shownIndex)
            this.updateInfoText(texts[shownIndex])
            return
        }

        if (this._infoTextObject.spinCounter < 2) {
            this._infoTextObject.spinCounter++

            return
        }

        this._infoTextObject.spinCounter = 0

        if (this._infoTextObject.shownIndexes.length === 4) {
            this._infoTextObject.shownIndexes = []
            this.updateInfoTextRandomly()
            return
        }
        let randomIndex: number = Math.floor(Math.random() * texts.length)

        while (this._infoTextObject.shownIndexes.includes(randomIndex)) {
            randomIndex = Math.floor(Math.random() * texts.length)
        }

        this._infoTextObject.spinCounter++
        this._infoTextObject.shownIndexes.push(randomIndex)
        this.updateInfoText(texts[randomIndex])
    }

    private displayUI = (value: boolean) => {
        UIAPI.emit(UIGameEvent.show_ui, { value: value })
    }
    //UPDATE
    private onBetHasChanged = (value: number, baseValue: number) => {
        UIAPI.emit(UIGameEvent.bet, { value: value, base_value: baseValue })
    }

    private onBetPlusClicked = () => {
        this.stepBet(1)
    }

    private onBetMinusClicked = () => {
        this.stepBet(-1)
    }

    private stepBet(step: 1 | -1) {
        const maxBet = Number(this._model.maxBet || 0)
        const availableBets = [...new Set(this._model.bets)]
            .filter((bet) => Number.isFinite(bet) && bet > 0)
            .filter((bet) => (maxBet > 0 ? bet <= maxBet : true))
            .sort((a, b) => a - b)

        if (!availableBets.length) {
            return
        }

        const currentBet = Number(this._model.currentBet)
        const exactIndex = availableBets.indexOf(currentBet)

        let targetBet = currentBet

        if (exactIndex >= 0) {
            const nextIndex = Math.min(Math.max(exactIndex + step, 0), availableBets.length - 1)
            targetBet = availableBets[nextIndex]
        } else if (step > 0) {
            targetBet = availableBets.find((bet) => bet > currentBet) ?? availableBets[availableBets.length - 1]
        } else {
            const lowerOrEqual = availableBets.filter((bet) => bet < currentBet)
            targetBet = lowerOrEqual.length ? lowerOrEqual[lowerOrEqual.length - 1] : availableBets[0]
        }

        if (targetBet !== currentBet) {
            GameSignals.changeBet.emit(targetBet)
        }
    }

    private onUIConnections = (data: any) => {
        const actionsAllowedWhenGameLocked = [
            UIEvent.set_volume,
            UIEvent.set_sound,
            UIEvent.set_music,
            UIEvent.set_spacebar_spin,
            UIEvent.set_turbo_spin,
            UIEvent.set_show_splash,
            UIEvent.menu_state,
            UIEvent.dialogs_state,
        ]

        if (this._isGameLocked && !actionsAllowedWhenGameLocked.includes(data.action)) return

        switch (data.action) {
            case UIEvent.set_bet:
                GameSignals.changeBet.emit(data.data.value)
                break

            case UIEvent.set_autoplay:
                if (this._operatorConfig.noAutoPlay === true) {
                    break
                }
                if (data.data.value > 0) {
                    GameSignals.startAutoplay.emit(data.data.value)
                } else {
                    GameSignals.stopAutoplay.emit()
                }
                break

            case UIEvent.set_stop_single_win:
                GameSignals.setAutoplaySettings.emit({ singleWin: data.data.value })
                break

            case 'set_autoplay_stop_any_win':
                GameSignals.setAutoplaySettings.emit({ stopOnAnyWin: !!data.data.value })
                break

            case UIEvent.set_stop_balance_lower:
                GameSignals.setAutoplaySettings.emit({ balanceLower: data.data.value })
                break

            case UIEvent.set_stop_balance_higher:
                GameSignals.setAutoplaySettings.emit({ balanceHigher: data.data.value })
                break

            case UIEvent.set_autoplay_stop_cash_decrease_enabled:
                GameSignals.setAutoplaySettings.emit({ balanceLowerEnabled: !!data.data.value })
                break

            case UIEvent.set_autoplay_stop_cash_increase_enabled:
                GameSignals.setAutoplaySettings.emit({ balanceHigherEnabled: !!data.data.value })
                break

            case UIEvent.set_autoplay_stop_cash_decrease:
                GameSignals.setAutoplaySettings.emit({ balanceLower: data.data.value })
                break

            case UIEvent.set_autoplay_stop_cash_increase:
                GameSignals.setAutoplaySettings.emit({ balanceHigher: data.data.value })
                break

            case UIEvent.set_spacebar_spin:
                this._isSpacebarActive = data.data.value
                UIAPI.emit(UIGameEvent.spacebar_spin, { value: this._isSpacebarActive })
                RestoreUtil.setKeyData(RestoreUtil.SPACEBAR_SPIN_KEY, `${+data.data.value}`)
                break
            case UIEvent.set_show_splash:
                GameSignals.skipIntroToggled.emit(!data.data.value)
                break
            case UIEvent.set_sound:
                UIAPI.emit(UIGameEvent.sound, { value: data.data.value })
                GameSignals.toggleSoundGroup.emit('sounds', data.data.value)
                RestoreUtil.setKeyData(RestoreUtil.SOUND_KEY, `${+data.data.value}`)
                this.syncVolumeWithLegacyToggles(data.data.value, Facade.defaultConfig?.music ?? true)
                break

            case UIEvent.set_music:
                UIAPI.emit(UIGameEvent.music, { value: data.data.value })
                GameSignals.toggleSoundGroup.emit('music', data.data.value)
                RestoreUtil.setKeyData(RestoreUtil.MUSIC_KEY, `${+data.data.value}`)
                this.syncVolumeWithLegacyToggles(Facade.defaultConfig?.sound ?? true, data.data.value)
                break

            case UIEvent.set_volume: {
                const volume = this.clampVolume(Number(data.data.value))
                const isAudioEnabled = volume > 0

                UIAPI.emit(UIGameEvent.volume, { value: volume })
                UIAPI.emit(UIGameEvent.sound, { value: isAudioEnabled })
                UIAPI.emit(UIGameEvent.music, { value: isAudioEnabled })

                GameSignals.setMasterVolume.emit(volume)

                RestoreUtil.setKeyData(RestoreUtil.VOLUME_KEY, `${volume}`)
                RestoreUtil.setKeyData(RestoreUtil.SOUND_KEY, `${+isAudioEnabled}`)
                RestoreUtil.setKeyData(RestoreUtil.MUSIC_KEY, `${+isAudioEnabled}`)
                break
            }

            case UIEvent.clicked_play:
            case UIEvent.hold_play_end:
                if (Facade.requestOngoing || Facade.isPopupBlockingInput) return
                GameSignals.spinButtonClicked.emit()
                break
            case UIEvent.clicked_home:
            case UIEvent.clicked_lobby:
                Utils.goToLobby()
                break

            case UIEvent.clicked_win_history:
                window.open('', '_blank')

                break

            case UIEvent.clicked_stop:
                if (Facade.isPopupBlockingInput) return
                GameSignals.stopButtonClicked.emit()
                break

            case UIEvent.menu_state:
                this._isMenuShown = data.data.value
                break

            case UIEvent.dialogs_state:
                this._isDialogShown = data.data.value
                GameSignals.changeDialogState.emit(this._isDialogShown)
                break

            case UIEvent.clicked_button:
                GameSignals.buttonClicked.emit(data.data.name)
                break

            case UIEvent.bonus_buy_clicked:
                if (this._currentUIState === 'ready') {
                    if (data.data.id == 2) {
                        //PREMIUM SPINS
                        GameSignals.premiumSpinsToggled.emit(!this._model.isPremiumSpinsActive)
                    } else if (data.data.id == 1) {
                        //BONUS BUY
                        if (!this.isBuyBonusEnabledForRuntime(this._operatorConfig)) {
                            break
                        }
                        if (this._model.isPremiumSpinsActive) {
                            GameSignals.premiumSpinsToggled.emit(false)
                        }
                        GameSignals.buyBonusClicked.emit(data.data.id)
                    }
                    UIAPI.emit(UIGameEvent.close_menu, {})
                    GameSignals.updateUIWinField.emit('')
                }
                break

            case UIEvent.set_turbo_spin:
                if (this._operatorConfig.isQuickSpin === false || this._operatorConfig.noTurboSpin === true) {
                    break
                }
                this.toggleTurboSpin(data.data.value)
                break

            case UIEvent.set_bonus_mode:
                GameSignals.premiumSpinsToggled.emit(data.data.value)
                break
        }
    }

    private toggleTurboSpin = (state: boolean) => {
        Facade.isTurboSpin = state
        Facade.isQuickSpin = state
        this._isQuickSpinActive = state

        UIAPI.emit(UIGameEvent.turbo_spin, { value: state })
        GameSignals.turboToggled.emit(state)
        RestoreUtil.setKeyData(RestoreUtil.TURBO_SPIN_KEY, `${+state}`)
    }

    private onLockGamePanel = () => {
        this.setGameState('playing')
    }

    private onUnlockGamePanel = () => {
        this.setGameState('ready')
    }

    private onStartSpin = (bet: number) => {
        this.updateFreeSpinsInfo()
        this._uiPanelRoundWinTotal = 0
        this.clearWinningComboTickerDelay()

        this.setGameState('playing')

        if (this._model.roundState.isFreeSpins) {
            return
        }

        GameSignals.updateUIWinField.emit('')

        const provisionalDeduction = this._model.bonusBuyBet ? 0 : bet / 100
        if (this._model.balance - provisionalDeduction < 0 || this._model.freeRoundsActive()) {
            return
        }

        const provisionalBalance = this._model.balance - provisionalDeduction

        UIAPI.emit(UIGameEvent.balance, { value: provisionalBalance })
        GameSignals.balanceHasChanged.emit(provisionalBalance)
        this.updateInfoText(UIManager.LEGACY_SPIN_START_TEXT)
    }

    private onShowRegularWin = (data: ShowRegularWinningsData) => {
        if (!data?.win || data.win <= 0) {
            return
        }

        this.clearWinningComboTickerDelay()
        this._winningComboTickerDelay = gsap.delayedCall(1, () => {
            const formattedWin = toMoney(toMajorUnits(data.win), 2)
            this.updateInfoText(`${UIManager.LEGACY_WINNING_COMBO_PREFIX} ${formattedWin}`)
        })
    }

    private onUpdateWinPanelValueForUi = (value: number, duration: number = -1, force: boolean = false) => {
        if (value < 0) {
            return
        }

        if (value === 0 && !force) {
            return
        }

        this._uiPanelRoundWinTotal += value
        UIAPI.emit(UIGameEvent.update_win_info, { text: toMoney(this._uiPanelRoundWinTotal, 2) })
    }

    private onBonusStart = () => {
        this.stopAutoPlay()
    }

    private onPromoFreeSpinsAccepted = () => {
        GameSignals.spinButtonClicked.emit()
    }

    private onBonusStopComplete = () => {
        gsap.to(this, {
            duration: 1.5,
            onComplete: () => {
                this.setGameState('ready')
            },
        })
    }

    private onStopSpin = () => {
        const isPromoSpins = this._model.isPromoSpinsRound()

        if (
            Facade.isAuto ||
            this._model.freeRoundsActive() ||
            this._model.roundState.isFreeSpins ||
            (this._model.spinData.freebet &&
                this._model.spinData.freebet.amount_left === this._model.spinData.freebet.total_amount_given) ||
            this._model.isFreeSpinsRestore ||
            isPromoSpins
        ) {
            this.setGameState('ready', {
                bonus_buy: 'disabled',
                autoplay: 'disabled',
                autoplay_stop: 'disabled',
                bet: 'disabled',
            })

            return
        }

        if (!this._model.spinData.freeround) {
            this.setGameState('ready')
        }
    }

    private onRoundStop = () => {
        if (!this._model.freeRoundsActive() && this._model.spinData.freeround) {
            GameSignals.stopAutoplay.emit()
            GameSignals.freeRoundsCompleted.emit()

            if (Utilities.isLocalStorageAvailable()) {
                localStorage.removeItem(`${Facade.urlParams.playerUid}_freeround`)
            }

            this._infoTextObject.textCounter = 0
            this._infoTextObject.spinCounter = 0
            this._infoTextObject.shownIndexes = []
            this.updateInfoTextRandomly()
        }

        if (this._model.freeRoundsActive() || this._model.roundState.isFreeSpins) {
            this.setGameState(this._currentUIState, {
                bet: 'disabled',
                bonus_buy: 'disabled',
            })
        }
    }

    private onBonusStartComplete = () => {
        if (this._model.roundState.isFreeSpins) {
            const totalSpinsCount = this._model.spinData.freebet.total_amount_given
            const playedSpins = Math.min(totalSpinsCount - this._model.spinData.freebet.amount_left, totalSpinsCount)
            const counterText = TranslationUtils.translate('freespinCounter', [playedSpins, totalSpinsCount])
            const size: number = Platform.isMobile() ? 15 : UIManager.UI_INFO_TEXT_SIZE
            this.updateInfoText(counterText, size)
            gsap.killTweensOf(this._infoTextObject)
        }
    }

    private updateFreeSpinsInfo = (restored: boolean = false) => {
        if (this._model.roundState.isFreeSpins) {
            const totalSpinsCount = this._model.spinData.freebet.total_amount_given
            const playedSpins = Math.min(
                totalSpinsCount - this._model.spinData.freebet.amount_left + (restored ? 0 : 1),
                totalSpinsCount
            )
            const counterText = TranslationUtils.translate('freespinCounter', [playedSpins, totalSpinsCount])
            const size: number = Platform.isMobile() ? 15 : UIManager.UI_INFO_TEXT_SIZE
            this.updateInfoText(counterText, size)
            gsap.killTweensOf(this._infoTextObject)
        }
    }

    private onFreeRoundSpin = (restored: boolean = false) => {
        if (this._model.freeRoundsActive() && !this._model.roundState.isFreeSpins) {
            this.setGameState(this._currentUIState, {
                bet: 'disabled',
                bonus_buy: 'disabled',
            })
            const counterText = TranslationUtils.translate('freeRoundDisplay', [
                this._model.spinData.freeround.total_amount_given -
                    this._model.spinData.freeround.amount_left +
                    (restored ? 0 : 1),
                this._model.spinData.freeround.total_amount_given,
            ])

            const size: number = Platform.isMobile() ? 15 : UIManager.UI_INFO_TEXT_SIZE
            this.updateInfoText(counterText, size)
            gsap.killTweensOf(this._infoTextObject)
        }
    }

    private onFreeSpinsSummary = () => {
        this._infoTextObject.textCounter = 0
        this._infoTextObject.spinCounter = 0
        this._infoTextObject.shownIndexes = []
        this.updateInfoTextRandomly()
    }

    private normalizeWinInfoText = (value: string): string => {
        const normalized = String(value ?? '').trim()

        if (!normalized) {
            return ''
        }

        // Fallback for legacy/raw producers that still send minor units as plain integers.
        if (/^-?\d+$/.test(normalized)) {
            return toMoney(toMajorUnits(Number(normalized)), 2)
        }

        return normalized
    }

    private onUpdateUIWinField = (value: string) => {
        const normalizedValue = this.normalizeWinInfoText(value)
        UIAPI.emit(UIGameEvent.update_win_info, { text: normalizedValue })
        ;(GameSignals.winInfoUpdate, { text: normalizedValue })
    }

    private onBalanceUpdate = (value: number) => {
        UIAPI.emit(UIGameEvent.balance, { value: value })
    }

    private onServerError = () => {
        this._isGameLocked = true
        this._currentUIState = 'ready'
        this.setGameState('ready')

        UIAPI.emit(UIGameEvent.balance, { value: this._model.balance })
    }

    private mute = () => {
        UIAPI.emit(UIEvent.set_volume, { value: 0 })
    }

    private unMute = () => {
        UIAPI.emit(UIEvent.set_volume, { value: UIManager.UNMUTE_VOLUME })
    }

    private clampVolume = (value: number): number => {
        if (!Number.isFinite(value)) {
            return UIManager.DEFAULT_VOLUME
        }

        return Math.max(0, Math.min(1, value))
    }

    private syncVolumeWithLegacyToggles = (soundEnabled: boolean, musicEnabled: boolean) => {
        const currentVolume = Number(RestoreUtil.getKeyData(RestoreUtil.VOLUME_KEY))
        const fallbackVolume =
            typeof Facade.defaultConfig?.volume === 'number' ? Facade.defaultConfig.volume : UIManager.UNMUTE_VOLUME

        if (!soundEnabled && !musicEnabled) {
            UIAPI.emit(UIGameEvent.volume, { value: 0 })
            GameSignals.setMasterVolume.emit(0)
            RestoreUtil.setKeyData(RestoreUtil.VOLUME_KEY, '0')
            return
        }

        if (!Number.isFinite(currentVolume) || currentVolume <= 0) {
            const restoredVolume = this.clampVolume(fallbackVolume > 0 ? fallbackVolume : UIManager.UNMUTE_VOLUME)
            UIAPI.emit(UIGameEvent.volume, { value: restoredVolume })
            GameSignals.setMasterVolume.emit(restoredVolume)
            RestoreUtil.setKeyData(RestoreUtil.VOLUME_KEY, `${restoredVolume}`)
        }
    }

    private onTogglePaytable = () => {
        if (this._isMenuShown) {
            UIAPI.emit(UIGameEvent.close_menu)
        } else {
            UIAPI.emit(UIGameEvent.show_menu, { id: 'paytable', nav: 'info' })
        }
    }

    private onToggleGameHelp = () => {
        if (this._isMenuShown) {
            UIAPI.emit(UIGameEvent.close_menu)
        } else {
            UIAPI.emit(UIGameEvent.show_menu, { nav: 'info' })
        }
    }

    private setGameState = (state: string, mods?: any) => {
        this._currentUIState = state
        UIAPI.emit(UIGameEvent.game_state, {
            state: this._currentUIState,
            mods,
        })
    }

    private updateInfoText = (text: string, size: number = UIManager.UI_INFO_TEXT_SIZE) => {
        UIAPI.emit(UIGameEvent.update_info_text, { text, size, white_space: 'unset' })
        this._infoTextObject.text = text
    }

    private getLegacyLikeIdlePrompt = (): string => {
        const translated = TranslationUtils.translate('uiText1')
        return translated === 'uiText1' ? UIManager.LEGACY_IDLE_TEXT : translated
    }

    private clearWinningComboTickerDelay = () => {
        if (!this._winningComboTickerDelay) {
            return
        }

        this._winningComboTickerDelay.kill()
        this._winningComboTickerDelay = null
    }
}
